import { Injectable } from "@angular/core";
import { environment } from "environments/environment";
import { Socket } from "ngx-socket-io";
import { Observable, Subject, Subscription } from "rxjs";
import { DateService } from "../../../interceptors/date.service";
import { AuthService } from "../../auth/services/auth.service";
import { Downtime } from "../models/downtimes.model";
import { Report } from "../models/report.model";
import { State } from "../models/state.model";
import { ReportApiService } from "./report-api.service";

@Injectable({ providedIn: "root" })
export class ReportSocketService extends Socket {
  /** Flux bruts depuis le serveur */
  stateMessage$ = this.fromEvent("state");
  newReportMessage$ = this.fromEvent("new_report");

  /** Gestion des subscriptions ouvertes par start()/stop() */
  private sub: Subscription | null = null;

  /** Expose l’ID du nouveau report poussé par le back (pour le composant) */
  private newReportSubj = new Subject<Report>();
  readonly newReport$: Observable<Report> = this.newReportSubj.asObservable();

  constructor(
    authService: AuthService,
    private reportApiService: ReportApiService,
  ) {
    let url: string;
    if (location.host.includes("localhost")) {
      const aristoteUrl = new URL(environment.aristoteApi());
      const proto = aristoteUrl.protocol === "https:" ? "wss:" : "ws:";
      url = `${proto}//${aristoteUrl.host}`;
    } else {
      const proto = location.protocol === "https:" ? "wss:" : "ws:";
      url = `${proto}//${location.host}`;
    }
    // eslint-disable-next-line no-console
    console.log("device ws url:", url);

    super({
      url,
      options: {
        path: "/device-ws/dashboard/",
        query: { token: authService.ssoSignal()?.token },
        transports: ["websocket"],
      },
    });
  }

  /** Abonne les listeners 'state' (live) et 'new_report' (après ask_refresh) */
  start() {
    this.sub?.unsubscribe();
    this.sub = new Subscription();

    // 1) Suivi live d’un report en cours
    this.sub.add(
      this.stateMessage$.subscribe((resp: string) => {
        try {
          if (!resp) return;

          const result: {
            report: Report;
            currentState: State;
            downtimes: Downtime[];
          } = JSON.parse(resp);

          DateService.convertToDate(result);

          const oldReport = this.reportApiService._currentReport.value;

          // Ne traiter que les events du report actuellement affiché
          // (les events des autres reports sont gérés par LiveProductionService)
          if (oldReport?.id && result.report?.id && result.report.id !== oldReport.id) return;

          const newReport: Report = {
            ...oldReport,
            ...result.report,
            currentState: result.currentState,
            downtimes: result.downtimes,
            states: [...(oldReport?.states ?? []), result.currentState],
          };

          // Met à jour le report courant quand on est en live (refresh 2s)
          this.reportApiService.setCurrentReport(newReport);
        } catch (e) {
          // eslint-disable-next-line no-console
          console.error("parsing report error", e);
        }
      }),
    );

    // 2) Nouveau report poussé par le serveur après 'ask_refresh'
    // Le back renvoie of({ event: 'new_report', data: report.id })
    this.sub.add(
      this.newReportMessage$.subscribe((payload: any) => {
        const newReport: Report = payload?.data;

        if (!newReport) return;

        // ⚠️ On NE charge PAS ici : on laisse le composant ouvrir la popup
        this.newReportSubj.next(newReport);
      }),
    );
  }

  /** Coupe proprement toutes les subscriptions */
  stop() {
    this.sub?.unsubscribe();
    this.sub = null;
  }

  /** Suivi “temps réel” du report en cours (front → back) */
  refresh(reportId: string): void {
    this.emit("refresh", reportId);
  }

  /** Demande de génération/actualisation d’un nouveau report (front → back) */
  askRefresh(report: Report): void {
    // Le back répondra par un event 'new_report' avec l’ID du nouveau report
    this.emit("ask_refresh", report);
  }
}
